import CustomTable from "@/components/CustomTable";
import {
  getMerchantTeams,
  getTransactionReferral,
  PostReferral,
} from "@/services/Settings.service";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { columnsT } from "./columns";
import { Button } from "@/components/ui/button";
import { IoFunnelOutline } from "react-icons/io5";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Loader2, UserPlus } from "lucide-react";
import Invite from "./common/Invite";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router";
import { HiOutlineSlash } from "react-icons/hi2";
import CustomPageHeader from "../common/CustomPageHeader";
import CustomTabs from "@/components/CustomTabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import Selector from "@/components/Selector";
import { Separator } from "@/components/ui/separator";
import { ToastMessage, type } from "@/lib/toastr";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const Marketing = () => {
  const [filter, setFilter] = useState({
    page: 1,
    search: "",
  });
  const { data, isLoading, isFetching, refetch, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["teams"],
      queryFn: async ({ pageParam = filter.page }) =>
        await getMerchantTeams(pageParam, filter),
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length === 0) return undefined;
        return pages.length + 1;
      },
      placeholderData: keepPreviousData,
    });

  const dataArr = data?.pages?.map((page) => page.data.data.data).flat();
  const meta = data?.pages?.map((page) => page.data.data.meta).flat()[0];

  const handlePageChange = (page) => {
    setFilter({ ...filter, page: page });
  };

  return (
    <div className="flex flex-col gap-8 p-6">
      <div>
        <Breadcrumb>
          <BreadcrumbList className="gap-2">
            <BreadcrumbItem>
              <Link
                to="/dashboard/settings"
                className="flex items-center gap-1 text-sm text-primary0"
              >
                <ChevronLeft className="w-3 h-3" />
                Settings
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <HiOutlineSlash />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink className="flex items-center gap-1 text-sm text-gray-60">
                Marketing
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <CustomPageHeader
        title="Marketing"
        description="Update essential business details and company information"
      />
      <div className="container mx-auto">
        <CustomTabs
          tabs={["Transaction Discount", "Sign-up bonus"]}
          tabData={{
            "Transaction Discount": <TransactionDiscount />,
            "Sign-up bonus": <SigUpBonus />,
          }}
        />
      </div>
    </div>
  );
};

export default Marketing;

const TransactionDiscount = () => {
  const [isEnabled, setIsEnabled] = useState(Boolean);
  const [amount, setAmount] = useState(Number);
  const [count, setCount] = useState(Number);
  const [beneficiary, setBeneficiary] = useState("");

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["getTransactionReferral"],
    queryFn: async () => await getTransactionReferral(),
    refetchOnMount: "always",
  });
  const q = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => PostReferral(data),
    mutationKey: ["TransactionReferral"],
    onSuccess: (res) => {
      q.clear();
      ToastMessage(type.SUCCESS, "Settings has been saved successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const onSubmit = async () => {
    await mutation.mutate({
      status: isEnabled,
      amount,
      currency: "NGN",
      channel: "Transaction",
      beneficiary,
      order: count,
    });
  };

  const handleChange = (e) => {
    if (amount === 0 || beneficiary === "" || count === 0) {
      return;
    }

    mutation.mutate({
      status: e,
      amount,
      currency: "NGN",
      channel: "Transaction",
      beneficiary,
      order: count,
    });
  };

  useEffect(() => {
    if (data?.data?.data !== undefined) {
      const d = data?.data?.data?.filter((i) => i?.channel === "Transaction");
      setIsEnabled(d[0]?.status);
      setAmount(d[0]?.amount);
      setCount(d[0]?.order);

      setBeneficiary(d[0]?.beneficiary);
    }
  }, [data?.data?.data]);
  return (
    <div className="flex justify-center items-center">
      <div className="bg-off-white-1 p-6 w-[450px] rounded-[12px] gap-4 grid">
        <div className="flex flex-col gap-1">
          <h1 className="text-primary-3 text-xl  font-medium ">
            Set up transaction Discount
          </h1>
          <p className="text-sm text-gray-80">
            Sign-up bonus shows the amount users will receive when someone signs
            up with their referral code.
          </p>
        </div>
        <div className="h-[62px] p-[10.82px] rounded-[10.82px] gap-[5.41px] bg-white border-[#E7EAEB] border-[0.68px] flex items-center justify-between">
          <div className="grid gap-[2px]">
            <p className="text-[#434A5B] text-sm font-medium">Status</p>
            <p className="text-[#434A5B] text-xs">
              The status determines if the bonus is activated or not.
            </p>
          </div>
          {isLoading ? (
            <Skeleton className="h-6 w-11" />
          ) : (
            <Switch
              checked={isEnabled}
              onCheckedChange={(e) => {
                setIsEnabled(e);
                handleChange(e);
              }}
            />
          )}
        </div>
        <div className="grid gap-5">
          {isLoading ? (
            <Skeleton className="h-[52px]" />
          ) : (
            <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 bg-white">
              <Label className="text-xs text-gray-70">Amount</Label>
              <Input
                className="p-0 border-none bg-transparent rounded-none h-fit w-full"
                placeholder="0.00"
                onChange={(e) => setAmount(e?.target?.value)}
                value={amount?.toString()}
              />
            </div>
          )}
          {isLoading ? (
            <Skeleton className="h-[52px]" />
          ) : (
            <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between bg-white">
              <Label className="text-xs text-gray-70">Beneficiary</Label>
              <Select
                onValueChange={(e) => {
                  setBeneficiary(e);
                }}
                value={beneficiary}
              >
                <SelectTrigger className="w-full h-4 p-0 text-sm text-left border-none">
                  <SelectValue
                    placeholder="Select a beneficiary "
                    className="text-sm"
                  />
                </SelectTrigger>
                <SelectContent>
                  {[
                    { label: "Referrer", value: "Referrer" },
                    {
                      label: "User (Person referred)",
                      value: "User",
                    },
                    { label: "Both", value: "Both" },
                  ]?.map((item) => (
                    <SelectItem value={item?.value}>{item?.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isLoading ? (
            <Skeleton className="h-[52px]" />
          ) : (
            <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 bg-white">
              <Label className="text-xs text-gray-70">Transaction number</Label>
              <Input
                className="p-0 border-none bg-transparent rounded-none h-fit w-full"
                onChange={(e) => setCount(e?.target?.value)}
                value={count?.toString()}
                placeholder="E.g 10"
              />
            </div>
          )}
        </div>
        <Separator />
        <Button
          className="w-40 ml-auto"
          onClick={onSubmit}
          disabled={mutation?.isPending}
        >
          {mutation?.isPending && <Loader2 className="animate-spin" />}
          Save Changes <ChevronRight />
        </Button>
      </div>
    </div>
  );
};
const SigUpBonus = () => {
  return (
    <div className="flex justify-center items-center">
      <div className="bg-off-white-1 p-6 w-[450px] rounded-[12px] gap-4 grid">
        <div className="flex flex-col gap-1">
          <h1 className="text-primary-3 text-xl  font-medium ">
            Set up sign up bonus
          </h1>
          <p className="text-sm text-gray-80">
            Sign-up bonus shows the amount users will receive when someone signs
            up with their referral code.
          </p>
        </div>
        <div className="h-[62px] p-[10.82px] rounded-[10.82px] gap-[5.41px] bg-white border-[#E7EAEB] border-[0.68px] flex items-center justify-between">
          <div className="grid gap-[2px]">
            <p className="text-[#434A5B] text-sm font-medium">Status</p>
            <p className="text-[#434A5B] text-xs">
              The status determines if the bonus is activated or not.
            </p>
          </div>
          <Switch />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 bg-white">
            <Label className="text-xs text-gray-70">Amount</Label>
            <Input
              className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
              placeholder="0.00"
            />
          </div>
          <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between bg-white">
            <Label className="text-xs text-gray-70">Beneficiary</Label>

            <Selector options={[]} />
          </div>
        </div>
        <Separator />
        <Button className="w-40 ml-auto">
          Save Changes <ChevronRight />
        </Button>
      </div>
    </div>
  );
};
